import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";
import {
  FADE_UP_INITIAL,
  FADE_UP_ANIMATE,
  VIEWPORT_ONCE,
} from "./animations";

// Renders the transparent product variants (bottles / cans) side-by-side.
// The grid layout adapts to the number of SKUs (1–4+ mapped to different max
// widths so a single SKU doesn't stretch across the whole viewport).
function gridForCount(n) {
  if (n === 1) return "grid-cols-1 max-w-md mx-auto";
  if (n === 2) return "grid-cols-2 max-w-3xl mx-auto";
  if (n === 3) return "grid-cols-3 max-w-4xl mx-auto";
  return "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
}

export default function BrandProductLine({ brand }) {
  const { t } = useT();
  const items = brand.variantImages;
  // If variants declare their own physical heightMm (real-world height), scale
  // each image proportionally so the row visually reflects the actual size
  // difference between SKUs (e.g. 1 L bottle towers over a 150 mL can).
  const anyHeights = items.some((v) => v.heightMm);
  const maxMm = anyHeights
    ? Math.max(...items.map((v) => v.heightMm || 0))
    : null;
  return (
    <section
      className="relative overflow-hidden border-b border-neutral-100"
      style={{ background: `${brand.palette.accent}0A` }}
    >
      {/* Ambient brand emblem watermark — applies to every brand product line */}
      {(brand.watermark || brand.logo) && (
        <img
          aria-hidden
          src={brand.watermark || brand.logo}
          alt=""
          className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] max-w-none opacity-[0.06] -rotate-6"
        />
      )}

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="flex items-baseline justify-between gap-6 mb-12">
          <div>
            <p className="overline" style={{ color: brand.palette.accent }}>
              {t("brandPage.productLine")}
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl text-[#001F39] leading-tight">
              {t("brandPage.sideBySide", { name: brand.name })}
            </h2>
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400 hidden md:block">
            {String(items.length).padStart(2, "0")} {t("brandPage.skus")}
          </p>
        </div>
        <div className={`grid gap-8 md:gap-10 items-end ${gridForCount(items.length)}`}>
          {items.map((v, i) => {
            const ratio = anyHeights && v.heightMm && maxMm ? v.heightMm / maxMm : 1;
            const imgStyle = anyHeights
              ? {
                  // Tallest SKU = ratio 1.0, everything else scales down accordingly.
                  maxHeight: `clamp(${Math.round(140 * ratio)}px, ${(38 * ratio).toFixed(2)}vw, ${Math.round(460 * ratio)}px)`,
                }
              : undefined;
            return (
              <motion.div
                key={v.src}
                initial={FADE_UP_INITIAL}
                whileInView={FADE_UP_ANIMATE}
                viewport={VIEWPORT_ONCE}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`brand-page-${brand.slug}-variant-${i}`}
                className="flex flex-col items-center text-center"
              >
                <img
                  src={v.src}
                  alt={v.label}
                  className={
                    anyHeights
                      ? "w-auto object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.18)]"
                      : "max-h-[380px] md:max-h-[460px] w-auto object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.18)]"
                  }
                  style={imgStyle}
                />
                <p
                  className="mt-5 text-xs md:text-sm uppercase tracking-widest font-semibold"
                  style={{ color: brand.palette.accent }}
                >
                  {v.label}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
